import {
  CATALOG_KINDS,
  CATALOG_KIND_LABELS,
  CONTACT_KINDS,
  SUPPLY_TYPES,
  SUPPLY_TYPE_LABELS,
  type CatalogEntry,
  type CatalogKind,
  type CatalogListResponse,
  catalogListResponseSchema,
  acknowledgmentResponseSchema,
} from '@sivan/shared';
import { askConfirm } from '../ui/confirm';
import { useEffect, useId, useState } from 'react';
import { ApiError, NetworkError, apiGet, apiPut } from '../api/client';
import { GeneralChecksEditor } from './GeneralChecksEditor';
import { AcknowledgmentEditor } from './AcknowledgmentEditor';
import { InspectionTemplateEditor } from './InspectionTemplateEditor';
import { Modal } from './Modal';
import {
  type CatalogRow,
  blankRow,
  isDirty,
  move,
  parseBulk,
  rowsFrom,
  toRequest,
  validateRows,
} from './catalog-draft';
import { reportPendingChanges, warnOnUnload, confirmDiscardPending } from './unsaved-changes';
import { useLoad } from './use-master-data';

// "הגדרות מסירות": the project catalogs that handover findings choose from (one shared list
// editor for the kinds, H-2), the inspection template (H-Checklist) and the general handover checks
// (H-Next).

type Tab = CatalogKind | 'template' | 'general' | 'acknowledgment';
const ORDER: readonly Tab[] = [
  'contractor',
  'work_category',
  'supplier',
  'area',
  'component',
  'template',
  'general',
  'acknowledgment',
];
const TAB_LABELS: Readonly<Record<Tab, string>> = {
  ...CATALOG_KIND_LABELS,
  template: 'תבנית בדיקה',
  general: 'בדיקות ומסירה כללית',
  acknowledgment: 'נוסח ההצהרה',
};
const SINGULAR: Readonly<Record<CatalogKind, string>> = {
  contractor: 'קבלן',
  supplier: 'ספק',
  work_category: 'תחום עבודה',
  area: 'חדר',
  component: 'רכיב',
};

/** A contractor's work categories: a compact multi-select (active ones, and any already chosen). */
function CategorySelect({
  categories,
  selected,
  disabled,
  contractor,
  onChange,
}: {
  readonly categories: readonly CatalogEntry[];
  readonly selected: readonly string[];
  readonly disabled: boolean;
  readonly contractor: string;
  readonly onChange: (ids: string[]) => void;
}) {
  const names = categories.filter((c) => selected.includes(c.id)).map((c) => c.name);
  const options = categories.filter((c) => c.active || selected.includes(c.id));
  return (
    <div className="field catalog-row__categories">
      <span>תחום עבודה</span>
      <details className="category-select">
        <summary aria-label={`תחומי עבודה של ${contractor}: ${names.join(', ') || 'לא נבחרו'}`}>
          {names.length === 0 ? 'בחירת תחום' : names.join(', ')}
        </summary>
        <fieldset disabled={disabled}>
          <legend className="visually-hidden">תחומי עבודה של {contractor}</legend>
          {options.length === 0 ? (
            <p className="hint">אין תחומי עבודה. הוסיפו אותם בלשונית "תחומי עבודה".</p>
          ) : (
            options.map((category) => (
              <label key={category.id} className="category-select__option">
                <input
                  type="checkbox"
                  checked={selected.includes(category.id)}
                  onChange={(event) => {
                    onChange(
                      event.target.checked
                        ? [...selected, category.id]
                        : selected.filter((id) => id !== category.id),
                    );
                  }}
                />
                {category.name}
                {!category.active && ' (מושבת)'}
              </label>
            ))
          )}
        </fieldset>
      </details>
    </div>
  );
}

type SaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'saved' }
  | { readonly kind: 'conflict' }
  | { readonly kind: 'error'; readonly text: string };

/** A work category's default contractor: one of the active contractors linked to it. */
function DefaultContractorSelect({
  category,
  categoryId,
  contractors,
  value,
  disabled,
  onChange,
}: {
  readonly category: string;
  readonly categoryId: string | null;
  readonly contractors: readonly CatalogEntry[];
  readonly value: string | null;
  readonly disabled: boolean;
  readonly onChange: (id: string | null) => void;
}) {
  const options = contractors.filter(
    (c) =>
      categoryId !== null && c.categoryIds.includes(categoryId) && (c.active || c.id === value),
  );
  return (
    <label className="field catalog-row__default">
      <span>קבלן ברירת מחדל</span>
      <select
        value={value ?? ''}
        disabled={disabled || categoryId === null}
        aria-label={`קבלן ברירת מחדל: ${category}`}
        onChange={(e) => {
          onChange(e.target.value === '' ? null : e.target.value);
        }}
      >
        <option value="">
          {categoryId === null
            ? 'אחרי שמירת התחום'
            : options.length === 0
              ? 'אין קבלנים בתחום'
              : 'ללא (בחירה ידנית)'}
        </option>
        {options.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
            {!c.active && ' (מושבת)'}
          </option>
        ))}
      </select>
    </label>
  );
}

function CatalogEditorBody({
  projectId,
  kind,
  list,
  categories,
  contractors,
  justSaved,
  onSaved,
  onReload,
}: {
  readonly projectId: string;
  readonly kind: CatalogKind;
  readonly list: CatalogListResponse;
  /** Work categories, for the contractors' category column. */
  readonly categories: readonly CatalogEntry[];
  /** Contractors, for the work categories' default contractor. */
  readonly contractors: readonly CatalogEntry[];
  readonly justSaved: boolean;
  readonly onSaved: (list: CatalogListResponse) => void;
  readonly onReload: () => void;
}) {
  const [rows, setRows] = useState<CatalogRow[]>(() => rowsFrom(list));
  const [save, setSave] = useState<SaveState>({ kind: justSaved ? 'saved' : 'idle' });
  const [query, setQuery] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const editable = list.permissions.edit;
  const contacts = CONTACT_KINDS.includes(kind) && list.permissions.contacts;
  const dirty = isDirty(rows, list, kind);
  const errors = showErrors ? validateRows(rows, kind) : {};
  const searchId = useId();

  useEffect(() => {
    reportPendingChanges(dirty ? 1 : 0);
    window.addEventListener('beforeunload', warnOnUnload);
    return () => {
      window.removeEventListener('beforeunload', warnOnUnload);
      reportPendingChanges(0);
    };
  }, [dirty]);
  useEffect(() => {
    if (focusKey === null) return;
    document.getElementById(`catalog-name-${focusKey}`)?.focus();
    setFocusKey(null);
  }, [focusKey]);

  const change = (next: (r: CatalogRow[]) => CatalogRow[]) => {
    setRows(next);
    if (save.kind !== 'saving') setSave({ kind: 'idle' });
  };
  const update = (
    key: string,
    field: keyof CatalogRow,
    value: string | boolean | string[] | null,
  ) => {
    change((list) => list.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };
  const needle = query.trim().toLowerCase();
  const visible = needle === '' ? rows : rows.filter((r) => r.name.toLowerCase().includes(needle));
  const activeCount = rows.filter((r) => r.active).length;

  const submit = async () => {
    if (Object.keys(validateRows(rows, kind)).length > 0) {
      setShowErrors(true);
      setSave({ kind: 'error', text: 'יש שורות שדורשות תיקון. הרשימה לא נשמרה.' });
      return;
    }
    setSave({ kind: 'saving' });
    try {
      const saved = await apiPut(
        `/projects/${projectId}/catalogs/${kind}`,
        toRequest(rows, list.listVersion, kind, list.permissions.contacts),
        catalogListResponseSchema,
      );
      setShowErrors(false);
      setSave({ kind: 'saved' });
      onSaved(saved);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) setSave({ kind: 'conflict' });
      else if (error instanceof ApiError && error.status === 422)
        setSave({ kind: 'error', text: 'השרת דחה חלק מהשורות (למשל שם כפול). בדקו ונסו שוב.' });
      else if (error instanceof ApiError && error.status === 403)
        setSave({ kind: 'error', text: 'אין לך הרשאה לערוך רשימה זו.' });
      else if (error instanceof NetworkError)
        setSave({ kind: 'error', text: 'אין חיבור לשרת. השינויים עדיין כאן; נסו שוב.' });
      else setSave({ kind: 'error', text: 'השמירה נכשלה. השינויים עדיין כאן; נסו שוב.' });
    }
  };

  return (
    <div className="catalog-editor">
      <div className="catalog-toolbar">
        <label className="field catalog-toolbar__search" htmlFor={searchId}>
          <span>חיפוש</span>
          <input
            id={searchId}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
          />
        </label>
        <p className="hint catalog-toolbar__count">
          {activeCount} פעילים מתוך {rows.length}
        </p>
        {editable && (
          <div className="catalog-toolbar__actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                const row = blankRow();
                setQuery('');
                change((list) => [...list, row]);
                setFocusKey(row.key);
              }}
            >
              + הוספת {SINGULAR[kind]}
            </button>
            <button
              type="button"
              className="button"
              onClick={() => {
                setBulkOpen(true);
              }}
            >
              הוספה מרובה
            </button>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="empty">
          הרשימה ריקה.{editable ? ' הוסיפו ערכים כדי שיופיעו בטופס הליקוי.' : ''}
        </p>
      ) : visible.length === 0 ? (
        <p className="empty">לא נמצאו ערכים תואמים.</p>
      ) : (
        <ol className="catalog-list" aria-label={CATALOG_KIND_LABELS[kind]}>
          {visible.map((row) => {
            const index = rows.indexOf(row);
            const e = errors[row.key] ?? {};
            const name = row.name.trim() === '' ? 'ערך חדש' : row.name;
            const input = (
              field: 'contactName' | 'contactPhone' | 'contactEmail',
              label: string,
              extra: { dir?: 'ltr'; type?: string; inputMode?: 'tel' | 'email' } = {},
            ) => (
              <label className={`field catalog-row__${field}`}>
                <span>{label}</span>
                <input
                  value={row[field]}
                  disabled={!editable}
                  aria-invalid={e[field] !== undefined}
                  {...extra}
                  onChange={(event) => {
                    update(row.key, field, event.target.value);
                  }}
                />
                {e[field] !== undefined && <p className="field__error">{e[field]}</p>}
              </label>
            );
            return (
              <li
                key={row.key}
                className={`catalog-row${row.active ? '' : ' catalog-row--inactive'}`}
                aria-label={name}
              >
                <span className="catalog-row__position" aria-hidden="true">
                  {index + 1}
                </span>
                {kind === 'contractor' && (
                  <CategorySelect
                    categories={categories}
                    selected={row.categoryIds}
                    disabled={!editable}
                    contractor={name}
                    onChange={(ids) => {
                      update(row.key, 'categoryIds', [...ids].sort());
                    }}
                  />
                )}
                <label className="field catalog-row__name">
                  <span>
                    {kind === 'contractor' ? 'שם הקבלן' : kind === 'supplier' ? 'שם הספק' : 'שם'}
                  </span>
                  <input
                    id={`catalog-name-${row.key}`}
                    value={row.name}
                    disabled={!editable}
                    aria-invalid={e.name !== undefined}
                    onChange={(event) => {
                      update(row.key, 'name', event.target.value);
                    }}
                  />
                  {e.name !== undefined && <p className="field__error">{e.name}</p>}
                </label>
                {kind === 'supplier' && (
                  <label className="field catalog-row__supply">
                    <span>סוג אספקה</span>
                    <select
                      value={row.supplyType ?? ''}
                      disabled={!editable}
                      aria-invalid={e.supplyType !== undefined}
                      aria-label={`סוג אספקה: ${name}`}
                      onChange={(event) => {
                        update(
                          row.key,
                          'supplyType',
                          event.target.value === '' ? null : event.target.value,
                        );
                      }}
                    >
                      <option value="">בחירה…</option>
                      {SUPPLY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {SUPPLY_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </select>
                    {e.supplyType !== undefined && <p className="field__error">{e.supplyType}</p>}
                  </label>
                )}
                {kind === 'work_category' && (
                  <DefaultContractorSelect
                    category={name}
                    categoryId={row.id}
                    contractors={contractors}
                    value={row.defaultContractorId}
                    disabled={!editable}
                    onChange={(contractorId) => {
                      update(row.key, 'defaultContractorId', contractorId);
                    }}
                  />
                )}
                {contacts && (
                  <>
                    {input('contactName', 'איש קשר')}
                    {input('contactPhone', 'טלפון', { dir: 'ltr', type: 'tel', inputMode: 'tel' })}
                    {input('contactEmail', 'דוא"ל', {
                      dir: 'ltr',
                      type: 'email',
                      inputMode: 'email',
                    })}
                  </>
                )}
                <label className="catalog-row__active" title="סטטוס">
                  <input
                    type="checkbox"
                    checked={row.active}
                    disabled={!editable}
                    onChange={(event) => {
                      update(row.key, 'active', event.target.checked);
                    }}
                  />
                  <span>פעיל</span>
                </label>
                {row.id === null && <span className="chip chip--new">חדש</span>}
                {editable && needle === '' && (
                  <span className="catalog-row__move">
                    <button
                      type="button"
                      className="button button--small"
                      aria-label={`העברה למעלה: ${name}`}
                      disabled={index === 0}
                      onClick={() => {
                        change((list) => move(list, row.key, -1));
                      }}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="button button--small"
                      aria-label={`העברה למטה: ${name}`}
                      disabled={index === rows.length - 1}
                      onClick={() => {
                        change((list) => move(list, row.key, 1));
                      }}
                    >
                      ▼
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {kind === 'work_category' && (
        <p className="hint">
          קבלן ברירת המחדל מוצע אוטומטית בליקוי חדש בתחום, וניתן להחליף אותו. בלי ברירת מחדל, כשיש
          בתחום כמה קבלנים, הבודק יתבקש לבחור. שיוך קבלנים לתחומים נעשה בלשונית "קבלנים מבצעים".
        </p>
      )}
      {kind === 'supplier' && (
        <p className="hint">
          ספקי חומרים לרוכשים (מטבחים, שיש, ריצוף). ספק אינו מבצע עבודה: הוא אינו משויך לתחומי עבודה
          ולא לליקויים.
        </p>
      )}
      {!CONTACT_KINDS.includes(kind) && (
        <p className="hint">
          ערכי התחלה לדוגמה מאב־הטיפוס הקודם; ניתן לשנות, להוסיף ולהשבית. ערך מושבת לא יוצע בליקוי
          חדש, וליקויים קיימים שומרים את השם שנרשם בהם.
        </p>
      )}
      {CONTACT_KINDS.includes(kind) && !list.permissions.contacts && (
        <p className="hint">פרטי אנשי הקשר מוצגים רק למורשים.</p>
      )}

      {editable && (
        <div className="save-bar" role="region" aria-label="שמירת הרשימה">
          {save.kind === 'conflict' ? (
            <div className="notice notice--error save-bar__notice" role="alert">
              <p>הרשימה עודכנה במקום אחר מאז שנפתחה. השינויים שלך לא נשמרו.</p>
              <button
                type="button"
                className="button button--small"
                onClick={() => {
                  void confirmDiscardPending().then((ok) => {
                    if (ok) onReload();
                  });
                }}
              >
                טעינת הגרסה העדכנית
              </button>
            </div>
          ) : save.kind === 'error' ? (
            <p className="notice notice--error save-bar__notice" role="alert">
              {save.text}
            </p>
          ) : null}
          <p className={`save-bar__status${dirty ? ' save-bar__status--dirty' : ''}`} role="status">
            {save.kind === 'saving'
              ? 'שומר…'
              : dirty
                ? 'יש שינויים שלא נשמרו'
                : save.kind === 'saved'
                  ? 'הרשימה נשמרה'
                  : 'כל השינויים שמורים'}
          </p>
          <div className="save-bar__actions">
            <button
              type="button"
              className="button"
              disabled={!dirty || save.kind === 'saving'}
              onClick={() => {
                void askConfirm({
                  title: 'לבטל את השינויים שלא נשמרו?',
                  confirmLabel: 'ביטול השינויים',
                  danger: true,
                }).then((yes) => {
                  if (!yes) return;
                  setRows(rowsFrom(list));
                  setShowErrors(false);
                  setSave({ kind: 'idle' });
                });
              }}
            >
              ביטול שינויים
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={!dirty || save.kind === 'saving'}
              onClick={() => {
                void submit();
              }}
            >
              שמירת הרשימה
            </button>
          </div>
        </div>
      )}

      <Modal
        title={`הוספה מרובה: ${CATALOG_KIND_LABELS[kind]}`}
        open={bulkOpen}
        onClose={() => {
          setBulkOpen(false);
        }}
      >
        <form
          className="bulk-form"
          onSubmit={(e) => {
            e.preventDefault();
            const added = parseBulk(bulkText, kind);
            if (added.length > 0) change((list) => [...list, ...added]);
            setBulkText('');
            setBulkOpen(false);
          }}
        >
          <label className="field">
            <span>
              {kind === 'contractor'
                ? 'שורה לכל קבלן או ספק. אפשר להדביק מ-Excel עמודות: שם, איש קשר, טלפון, דוא"ל.'
                : 'שורה לכל ערך. אפשר להדביק עמודה מ-Excel.'}
            </span>
            <textarea
              rows={8}
              value={bulkText}
              onChange={(e) => {
                setBulkText(e.target.value);
              }}
            />
          </label>
          <p className="hint">
            {parseBulk(bulkText, kind).length} שורות יתווספו לרשימה (יישמרו בלחיצה על "שמירת
            הרשימה").
          </p>
          <div className="form-actions">
            <button type="submit" className="button button--primary">
              הוספה לרשימה
            </button>
            <button
              type="button"
              className="button"
              onClick={() => {
                setBulkOpen(false);
              }}
            >
              ביטול
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function CatalogEditor({
  projectId,
  kind,
}: {
  readonly projectId: string;
  readonly kind: CatalogKind;
}) {
  const [version, setVersion] = useState(0);
  const [saved, setSaved] = useState<CatalogListResponse | null>(null);
  const [data, reload] = useLoad(`catalog:${projectId}:${kind}:${String(version)}`, () =>
    apiGet(`/projects/${projectId}/catalogs/${kind}`, catalogListResponseSchema),
  );
  // The contractors' tab also needs the work categories, and the categories' tab the contractors.
  const related: CatalogKind | null =
    kind === 'contractor' ? 'work_category' : kind === 'work_category' ? 'contractor' : null;
  const [categories] = useLoad(`catalog:${projectId}:related:${kind}:${String(version)}`, () =>
    related === null
      ? Promise.resolve([])
      : apiGet(`/projects/${projectId}/catalogs/${related}`, catalogListResponseSchema).then(
          (r) => r.items,
        ),
  );
  const list = saved ?? (data.state === 'ready' ? data.data : null);
  if (list === null || categories.state === 'loading') {
    if (data.state === 'forbidden')
      return <p className="notice notice--error">אין לך הרשאה לצפות ברשימה זו.</p>;
    if (data.state === 'error')
      return (
        <div className="notice notice--error" role="alert">
          <p>טעינת הרשימה נכשלה.</p>
          <button type="button" className="button" onClick={reload}>
            נסו שוב
          </button>
        </div>
      );
    return (
      <div className="skeleton" role="status" aria-label="טוען נתונים">
        <div className="skeleton__row" />
        <div className="skeleton__row" />
      </div>
    );
  }
  return (
    <CatalogEditorBody
      key={list.listVersion}
      projectId={projectId}
      kind={kind}
      list={list}
      categories={kind === 'contractor' && categories.state === 'ready' ? categories.data : []}
      contractors={kind === 'work_category' && categories.state === 'ready' ? categories.data : []}
      justSaved={saved !== null}
      onSaved={setSaved}
      onReload={() => {
        setSaved(null);
        setVersion((v) => v + 1);
      }}
    />
  );
}

/** "הגדרות מסירות": contractors and suppliers, work categories, areas, inspection components. */
export function HandoverSettings({
  projectId,
  initialTab = 'contractor',
}: {
  readonly projectId: string;
  /** The tab shown first (the project setup opens the template directly). */
  readonly initialTab?: Tab;
}) {
  const [kind, setKind] = useState<Tab>(initialTab);
  const id = useId();
  // Only a business Super Admin may see and enter the organization's wording (403 otherwise).
  const [acknowledgment] = useLoad('handover-acknowledgment', () =>
    apiGet('/handover-acknowledgment', acknowledgmentResponseSchema),
  );
  return (
    <section className="handover-settings" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className="section-title">
        הגדרות מסירות
      </h2>
      <p className="hint">
        הרשימות שמהן בוחרים בטופס הליקוי במסירה ראשונית וסופית, תבנית הבדיקה לפי חדרים והבדיקות
        הכלליות.
      </p>
      <div className="tabs" role="tablist" aria-label="רשימות">
        {ORDER.filter(
          (k) =>
            k === 'template' ||
            k === 'general' ||
            (k === 'acknowledgment' ? acknowledgment.state === 'ready' : CATALOG_KINDS.includes(k)),
        ).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            id={`${id}-tab-${k}`}
            aria-selected={k === kind}
            aria-controls={`${id}-panel`}
            className="tabs__tab"
            onClick={() => {
              if (k !== kind)
                void confirmDiscardPending().then((ok) => {
                  if (ok) setKind(k);
                });
            }}
          >
            {TAB_LABELS[k]}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${kind}`}>
        {kind === 'template' ? (
          <InspectionTemplateEditor projectId={projectId} />
        ) : kind === 'general' ? (
          <GeneralChecksEditor projectId={projectId} />
        ) : kind === 'acknowledgment' ? (
          acknowledgment.state === 'ready' ? (
            <AcknowledgmentEditor initial={acknowledgment.data} />
          ) : acknowledgment.state === 'loading' ? null : (
            <p className="notice notice--info">
              נוסח ההצהרה משותף לכל הפרויקטים, ומוזן על ידי מנהל־על עסקי של הארגון (עם אימות
              דו־שלבי). פנו אליו כדי להזין את הנוסח המאושר.
            </p>
          )
        ) : (
          <CatalogEditor key={kind} projectId={projectId} kind={kind} />
        )}
      </div>
    </section>
  );
}
